import { Lightbulb, X } from "lucide-react";
import { useState } from "react";

import { dismissAction, getDismissedActions } from "../../help/onboarding-state.js";
import {
  type GuidedAction,
  type PageGuide,
  type PageStateContext,
  visibleGuidedActions,
} from "../../help/page-guide.js";
import { Button } from "../ui/button.js";

/**
 * State-driven "Suggested next steps" for a page. Surfaces the guide's
 * guided actions whose predicate currently holds (and role allows), minus any
 * the user dismissed. Renders nothing when there's nothing to suggest.
 */
export function NextActions({
  guide,
  context,
  onAct,
}: {
  guide: PageGuide;
  context: PageStateContext;
  onAct: (action: GuidedAction) => void;
}) {
  const [dismissed, setDismissed] = useState<string[]>(() => getDismissedActions(guide.route));
  const actions = visibleGuidedActions(guide, context).filter(
    (action) => !dismissed.includes(action.id),
  );
  if (actions.length === 0) return null;

  function hide(id: string) {
    dismissAction(guide.route, id);
    setDismissed((current) => [...current, id]);
  }

  return (
    <section
      aria-labelledby="next-actions-heading"
      className="rounded-md border bg-info-bg p-4 shadow-sm"
    >
      <div className="flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-brand" aria-hidden="true" />
        <h2 id="next-actions-heading" className="text-sm font-semibold text-fg">
          Suggested next steps
        </h2>
      </div>
      <ul className="mt-3 space-y-2">
        {actions.map((action) => (
          <li
            key={action.id}
            className="flex items-start justify-between gap-3 rounded-md border bg-surface p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">{action.label}</p>
              <p className="text-xs text-fg-muted">{action.description}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button type="button" size="sm" onClick={() => onAct(action)}>
                Go
              </Button>
              <button
                type="button"
                aria-label={`Dismiss: ${action.label}`}
                onClick={() => hide(action.id)}
                className="text-fg-subtle transition-colors hover:text-fg"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
